import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import VoiceCallWidget from './VoiceCallWidget';
import { Loader2, PhoneOff, Mic } from 'lucide-react';

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '99, 102, 241';
};

const positionStyles = (position) => {
  const map = {
    'bottom-right': { bottom: '1.5rem', right: '1.5rem' },
    'bottom-left': { bottom: '1.5rem', left: '1.5rem' },
    'top-right': { top: '1.5rem', right: '1.5rem' },
    'top-left': { top: '1.5rem', left: '1.5rem' },
  };
  return map[position] || map['bottom-right'];
};

const getSettingsFromAgent = (agent, urlParams) => {
  const p = urlParams || new URLSearchParams();
  return {
    logoUrl: agent?.logo_voice_widget_logo_url || null,
    waveColor: p.get('waveColor')
      ? `#${p.get('waveColor').replace('#', '')}`
      : agent?.logo_voice_widget_wave_color || '#6366f1',
    position: p.get('position') || agent?.logo_voice_widget_button_position || 'bottom-right',
    pulseAnimation: agent?.logo_voice_widget_pulse_animation ?? true,
    size: Number(p.get('size')) || agent?.logo_voice_widget_size || 72,
  };
};

const RippleWaves = ({ color, active }) => {
  if (!active) return null;
  const rgb = hexToRgb(color);
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid rgba(${rgb}, 0.45)`,
            animation: 'logoVoiceWave 2.4s ease-out infinite',
            animationDelay: `${i * 0.8}s`,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
};

const LogoVoiceEmbedWidget = ({ agentId, viewMode: initialViewMode }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const voiceRef = useRef(null);

  const [viewMode] = useState(() => {
    if (initialViewMode) return initialViewMode;
    if (typeof window === 'undefined') return 'button';
    const m = new URLSearchParams(window.location.search).get('view');
    if (m === 'inline') return 'inline';
    return 'button';
  });

  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    api.get(`/agents/${agentId}`)
      .then(r => {
        setAgent(r.data);
        setSettings(getSettingsFromAgent(r.data));
      })
      .catch(() => setError('Agent not found'))
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => {
    if (viewMode !== 'button') return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = [html.style.background, body.style.background, root?.style?.background];
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    if (root) root.style.background = 'transparent';
    return () => {
      html.style.background = prev[0];
      body.style.background = prev[1];
      if (root) root.style.background = prev[2] || '';
    };
  }, [viewMode]);

  const handleVoiceStateChange = useCallback(({ isConnected: connected, isConnecting: connecting }) => {
    setIsConnected(connected);
    setIsConnecting(connecting);
  }, []);

  const handleButtonClick = async () => {
    if (isConnected || isConnecting) {
      await voiceRef.current?.endCall?.();
      return;
    }
    if (!voiceRef.current?.isReady) return;
    await voiceRef.current.startCall();
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 text-sm">{error}</div>;
  }

  const { logoUrl, waveColor, position, pulseAnimation, size } = settings;
  const pos = positionStyles(position);
  const sessionActive = isConnected || isConnecting;
  const showWaves = pulseAnimation;
  const showEndButton = sessionActive;
  const waveRgb = hexToRgb(waveColor);
  const iconSize = Math.round(size * 0.38);

  const widgetContent = (
    <>
      <style>{`
        @keyframes logoVoiceWave {
          0% { transform: scale(1); opacity: 0.55; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        @keyframes logoVoicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(${waveRgb}, 0.35); }
          50% { box-shadow: 0 0 0 10px rgba(${waveRgb}, 0); }
        }
        @keyframes logoVoiceSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <VoiceCallWidget
        ref={voiceRef}
        agentId={agentId}
        prefetchedAgent={agent}
        viewMode="headless"
        themeOverride={{ showTranscript: false }}
        onCallStateChange={handleVoiceStateChange}
      />

      <div
        style={{
          width: size,
          height: size,
          flexShrink: 0,
          ...(viewMode === 'button'
            ? { position: 'fixed', zIndex: 9999999, ...pos }
            : { position: 'relative' }),
        }}
      >
        <RippleWaves color={waveColor} active={showWaves} />

        <button
          type="button"
          onClick={handleButtonClick}
          aria-label={
            isConnecting ? 'Connecting call'
              : isConnected ? 'End call'
              : 'Start voice call'
          }
          style={{
            position: 'relative',
            zIndex: 2,
            width: size,
            height: size,
            borderRadius: '50%',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            overflow: 'hidden',
            backgroundColor: showEndButton ? '#ef4444' : '#ffffff',
            color: showEndButton ? '#ffffff' : undefined,
            boxShadow: showEndButton
              ? '0 4px 20px rgba(239,68,68,0.45)'
              : `0 4px 20px rgba(0,0,0,0.15), 0 0 0 3px rgba(${waveRgb}, 0.2)`,
            animation: isConnecting ? 'logoVoicePulse 1.5s ease-in-out infinite' : undefined,
            transition: 'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {showEndButton ? (
            isConnecting && !isConnected ? (
              <>
                <div
                  style={{
                    position: 'absolute',
                    inset: 8,
                    borderRadius: '50%',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#ffffff',
                    animation: 'logoVoiceSpin 0.75s linear infinite',
                  }}
                />
                <Mic size={iconSize} style={{ opacity: 0.9 }} />
              </>
            ) : (
              <PhoneOff size={iconSize} />
            )
          ) : logoUrl ? (
            <img
              src={logoUrl}
              alt={agent?.name || 'Company logo'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${waveColor}, #818cf8)`,
                color: '#fff',
              }}
            >
              <Mic size={iconSize} />
            </div>
          )}
        </button>
      </div>
    </>
  );

  if (viewMode === 'inline') {
    return (
      <div className="flex items-center justify-center p-6">
        {widgetContent}
      </div>
    );
  }

  return widgetContent;
};

export const LogoVoiceEmbedPage = ({ agentId }) => (
  <LogoVoiceEmbedWidget agentId={agentId} />
);

export default LogoVoiceEmbedWidget;
